import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { DateTime } from 'luxon';
import type { RecordOf } from 'immutable';
import { v4 as uuidv4 } from 'uuid';

import { chartOfAccountsSelectors, type chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { rootUiActions } from 'app-components/flux/root-ui';
import { scheduledTransactionsActions, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import {
  type ScheduledTransactionEditSubmitPayload,
  markOverflowItemsInSplit,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionEditForm';
import {
  type ScheduledTransaction,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsTypes } from 'app-components/flux/transactions';
import {
  doesSplitModeAllowPaymentOrnaments,
  splitToPlain,
  type PlainSplitProps,
} from 'app-components/flux/transactions/splitsHelpers';
import type { SplitProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import type { ProjectSelect } from 'components/ClientProjectSelect';
import { HandleEndOption } from 'components/BillPresentment/utils';
import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import { dispatchRequestDialog } from 'components/RequestDialog/dispatcher';
import { dispatchGeneratorDialog } from 'components/Transactions/GeneratorDialog/dispatcher';
import { showSplitDialog } from 'components/Transactions/SplitDialog/actions';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import type {
  ScheduledTransactionEditSurfaceMode,
  ScheduledTransactionEditViewModel,
} from '../components/ScheduledTransactionEditViewModel';
import { deriveScheduledTransactionSeriesType } from '../tabs/typeSelection';
import type { ScheduledTransactionEditFormAccessors } from '../types';
import { scheduledTransactionEditFormHooks } from './formHooks';
import { useScheduledTransactionEditFormFields } from './useScheduledTransactionEditFormFields';
import { useScheduledTransactionEditFormSubmit } from './useScheduledTransactionEditFormSubmit';

type SubmitPayloadHandler = (payload: ScheduledTransactionEditSubmitPayload) => void;

// Caps an awaited create so a swallowed or stuck request can't leave
// `awaitCreate`'s caller (e.g. BillScoutSetup's New Series drawer) spinning
// forever on close.
const CREATE_TIMEOUT_MS = 30000;

const withTimeout = async <T>(promise: Promise<T>, ms: number, message: string): Promise<T> => {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    handle = setTimeout(() => reject(new Error(message)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(handle);
  }
};

type Params = {
  fakeCreate?: boolean;
  initialShowAssignSplits?: boolean;
  initialTab?: number;
  onBack?: () => void;
  onClose: (isDirty?: boolean, forceClose?: boolean) => void;
  onRootModalDrawerDirtyChange?: (isDirty: boolean) => void;
  onSplitsChanged?: () => void;
  onSubmit?: () => void;
  open: boolean;
  rootModalDrawer?: RootModalDrawerOptions;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  scheduledTransactionPlain: ScheduledTransactionType;
  submitPayloadRef: MutableRefObject<SubmitPayloadHandler | null>;
  subscriptionInitial?: boolean;
  title?: string;
  /**
   * Skips the "Recurring transaction updated." Undo toast on the update
   * path only (create/delete still show theirs). BillScoutSetup sets this —
   * its Series details pane autosaves on every field edit, so without it
   * the toast would fire roughly once per edit.
   */
  suppressUpdateUndoToast?: boolean;
  /** Skips the creation confirmation/undo toast for an embedded create flow. */
  suppressCreateUndoToast?: boolean;
  /** Leaves setup-created series unverified until its confirmation screen. */
  deferUserVerification?: boolean;
  /**
   * Waits for the create dispatch to actually settle before calling
   * `onSubmit`/`onClose`, instead of firing immediately after dispatch —
   * BillScoutSetup's New Series drawer sets this so it can't close (looking
   * successful) ahead of a slow or failed create.
   */
  awaitCreate?: boolean;
  /** Called instead of `onSubmit`/`onClose` when an `awaitCreate` create fails or times out. */
  onCreateError?: () => void;
};

export function useScheduledTransactionEditViewModel({
  initialShowAssignSplits = false,
  initialTab = 0,
  onBack,
  onClose,
  onRootModalDrawerDirtyChange,
  onSplitsChanged,
  onSubmit,
  open,
  rootModalDrawer,
  scheduledTransaction: model,
  scheduledTransactionPlain,
  submitPayloadRef,
  subscriptionInitial = false,
  suppressCreateUndoToast = false,
  deferUserVerification = false,
  suppressUpdateUndoToast = false,
  awaitCreate = false,
  onCreateError,
  title = 'Edit Series',
}: Params): ScheduledTransactionEditViewModel {
  const dispatch = useAppDispatch();
  const create = scheduledTransactionEditFormHooks.useSelector(({ state }) => state.create);
  const currentDate = scheduledTransactionEditFormHooks.useSelector(({ state }) => state.currentDate);
  const initialDueOn = scheduledTransactionEditFormHooks.useSelector(({ state }) => state.initialDueOn);
  const minimumDate = useMemo(() => DateTime.min(initialDueOn, currentDate), [initialDueOn, currentDate]);
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const isFeatureDrawer = Boolean(
    useAppSelector((state) => {
      const featureFlags = featureFlagsSelectors.getFeatureFlags(state);
      return featureFlags.get('webDrawerInput');
    }),
  );
  const surfaceMode: ScheduledTransactionEditSurfaceMode = rootModalDrawer
    ? 'embedded-root-drawer'
    : isFeatureDrawer
      ? 'drawer'
      : 'dialog';
  const nestedRootModalDrawer = useMemo(
    () => rootModalDrawer ?? (isFeatureDrawer ? { width: 'Large' as const } : undefined),
    [isFeatureDrawer, rootModalDrawer],
  );
  const [tabValue, setTabValue] = useState(initialTab);
  const formSubmit = useScheduledTransactionEditFormSubmit({ setTabValue });
  const formManager = scheduledTransactionEditFormHooks.useManager();
  const handleFieldChange = useCallback(
    (path: string) => {
      formSubmit.handleFieldChange(path);
      onRootModalDrawerDirtyChange?.(formManager.isFormDirty());
    },
    [formManager, formSubmit, onRootModalDrawerDirtyChange],
  );
  const formFields = useScheduledTransactionEditFormFields({ onFieldChange: handleFieldChange });
  const form = useMemo<ScheduledTransactionEditFormAccessors>(() => {
    const accessors = {
      setters: formFields.setters,
      derived: formFields.derived,
      validation: formSubmit.validation,
    } as ScheduledTransactionEditFormAccessors;

    Object.defineProperty(accessors, 'values', {
      enumerable: true,
      get: () => formFields.values,
    });

    return accessors;
  }, [formFields, formSubmit.validation]);

  // Bill Connect only makes sense for series whose money is actually owed
  // (Bill/Subscription/Transfer) — an Income series has nothing to connect
  // a biller/BVA account to, so the tab is hidden for it regardless of BVA
  // eligibility.
  const seriesType = scheduledTransactionEditFormHooks.useSelector(({ state, store }) =>
    deriveScheduledTransactionSeriesType(store, state.values.transaction as TransactionProps),
  );
  const showBillTab = seriesType !== 'INCOME';

  const [showAssignSplits, setShowAssignSplits] = useState(Boolean(initialShowAssignSplits && splitSeriesFf));
  const assignSplitsChangedRef = useRef(false);
  const pendingAssignSplitsSplitRef = useRef<PlainSplitProps | null | undefined>(undefined);
  const pendingAssignSplitsCoaRef = useRef<chartOfAccountsTypes.ChartOfAccountProps | null | undefined>(undefined);
  const pendingAssignSplitsTagsRef = useRef<TransactionProps['tags'] | undefined>(undefined);
  const openAssignSplitsDialogRef = useRef<(() => void) | null>(null);
  const openedAssignSplitsDialogIdRef = useRef<string | null>(null);

  const source = window.location.pathname.replaceAll?.('/', '');

  const submitPayload = useCallback(
    (payload: ScheduledTransactionEditSubmitPayload) => {
      const {
        requestValues,
        create: payloadCreate,
        fakeCreate: payloadFakeCreate,
        frequency,
        frequencyDetails,
        tracking,
      } = payload;

      if (requestValues.id) {
        dispatch(
          scheduledTransactionsActions.updateScheduledTransaction(
            scheduledTransactionsTypes.mkScheduledTransactionPatch({
              ...requestValues,
              ...(payloadFakeCreate && { isUserVerified: true }),
            } as any),
            {
              ...(payloadFakeCreate || suppressUpdateUndoToast
                ? {}
                : { undo: { userMessage: 'Recurring transaction updated.' } }),
            },
          ),
        );
        tracker.track(tracker.events.changeSeries, {
          source,
          purpose: payloadCreate ? 'create' : 'edit',
          frequency,
          frequencyDetails,
          type: tracking.type,
          frequencyState: frequencyDetails?.alias ? 'changed' : 'default',
          matchCriteria: tracking.matchCriteria,
          excludeFromSpendingPlan: tracking.excludeFromSpendingPlan,
          excludeFromReports: tracking.excludeFromReports,
          payee: tracking.payee,
          category: tracking.category?.id
            ? chartOfAccountsSelectors.getCoaStringSelector(null, tracking.category, true)
            : 'uncategorized',
        });

        if (onSubmit) {
          onSubmit();
        } else {
          onClose(false);
        }
        return;
      }

      const createPayload = scheduledTransactionsTypes.mkScheduledTransaction({
        // The BillScoutSetup New Series drawer owns its client id before
        // creation. Preserve it so Review can select and focus the row
        // through the create response's server-id/client-id re-key.
        clientId: scheduledTransactionPlain.clientId ?? uuidv4().toUpperCase(),
        ...requestValues,
        isUserVerified: !deferUserVerification,
      } as any);
      const createMeta = suppressCreateUndoToast ? {} : { undo: { userMessage: 'Recurring transaction created.' } };

      const trackCreate = () =>
        tracker.track(tracker.events.changeSeries, {
          source,
          purpose: payloadCreate ? 'create' : 'edit',
          frequency,
          frequencyDetails,
          type: tracking.type,
          frequencyState: frequencyDetails?.alias ? 'changed' : 'default',
          matchCriteria: tracking.matchCriteria,
          excludeFromSpendingPlan: tracking.excludeFromSpendingPlan,
          excludeFromReports: tracking.excludeFromReports,
          payee: tracking.payee,
          category: tracking.category?.id
            ? chartOfAccountsSelectors.getCoaStringSelector(null, tracking.category, true)
            : 'uncategorized',
        });

      if (awaitCreate) {
        // Waits for the create to actually land before treating it as
        // successful — without this, a caller (e.g. NewSeriesDrawer) that
        // closes on `onSubmit` would look done ahead of a slow or failed
        // request, briefly (or permanently, on failure) missing its row.
        void withTimeout(
          actionHelpers.bindPromiseAction(dispatch, scheduledTransactionsActions.createScheduledTransaction)(
            createPayload,
            createMeta,
          ),
          CREATE_TIMEOUT_MS,
          'scheduled transaction create timed out',
        ).then(
          () => {
            trackCreate();
            if (onSubmit) {
              onSubmit();
            } else {
              onClose(false);
            }
          },
          () => onCreateError?.(),
        );
        return;
      }

      dispatch(scheduledTransactionsActions.createScheduledTransaction(createPayload, createMeta));
      trackCreate();

      if (onSubmit) {
        onSubmit();
      } else {
        onClose(false);
      }
    },
    [
      awaitCreate,
      dispatch,
      onClose,
      onCreateError,
      onSubmit,
      scheduledTransactionPlain.clientId,
      source,
      suppressCreateUndoToast,
      deferUserVerification,
      suppressUpdateUndoToast,
    ],
  );

  submitPayloadRef.current = submitPayload;

  useEffect(() => {
    if (open) {
      setShowAssignSplits(Boolean(initialShowAssignSplits && splitSeriesFf));
    }
  }, [
    initialShowAssignSplits,
    open,
    scheduledTransactionPlain?.id,
    scheduledTransactionPlain?.clientId,
    splitSeriesFf,
  ]);

  useEffect(() => {
    if (!showAssignSplits) {
      openedAssignSplitsDialogIdRef.current = null;
      return;
    }

    const dialogId = `SPLIT_DIALOG_SERIES_${scheduledTransactionPlain?.id || scheduledTransactionPlain?.clientId || 'new'}`;
    if (openedAssignSplitsDialogIdRef.current !== dialogId) {
      openAssignSplitsDialogRef.current?.();
    }
  }, [showAssignSplits, scheduledTransactionPlain?.id, scheduledTransactionPlain?.clientId]);

  const close = useCallback(() => {
    onClose(form.derived.dirty);
  }, [form.derived.dirty, onClose]);

  const closeClean = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const deleteFunction = useCallback(() => {
    dispatch(
      scheduledTransactionsActions.deleteScheduledTransaction(
        scheduledTransactionsTypes.mkScheduledTransactionPatch({
          clientId: model?.clientId,
          id: model?.id,
          isDeleted: true,
        }),
        { undo: { userMessage: 'Recurring transaction deleted.' } },
      ),
    );
    onClose(false, true);
  }, [dispatch, model?.clientId, model?.id, onClose]);

  const confirmDelete = useCallback(() => {
    showConfirmationDialog({
      id: 'delete-series-confirmation-edit-series',
      size: 'xs',
      title: 'Delete recurring series',
      content: `Deleting the recurring series for ${scheduledTransactionPlain?.transaction?.payee} will delete all future reminders for this series.`,
      confirmLabel: 'Delete',
      confirmButtonVariant: 'delete',
      confirmAction: deleteFunction,
      denyLabel: 'Cancel',
      denyButtonVariant: 'outlined',
    });
  }, [deleteFunction, scheduledTransactionPlain?.transaction?.payee]);

  const endSeriesFunction = useCallback(() => {
    HandleEndOption(dispatch, model, source, true);
    onClose(false, true);
  }, [dispatch, model, onClose, source]);

  const confirmEndSeries = useCallback(() => {
    showConfirmationDialog({
      id: 'end-series-confirmation-edit-series',
      size: 'sm',
      title: 'End series',
      content: 'Stops this Series from generating Reminders. It will be kept for your records.',
      confirmLabel: 'End Series',
      confirmButtonVariant: 'contained',
      confirmAction: endSeriesFunction,
      denyLabel: 'Cancel',
      denyButtonVariant: 'outlined',
    });
  }, [endSeriesFunction]);

  const noopSetCustomersAndProjects: React.Dispatch<React.SetStateAction<ProjectSelect>> = useCallback((_value) => {
    // Series split assignment does not support client/project selection.
  }, []);

  const setSeriesSplit = useCallback(
    (val: RecordOf<SplitProps> | null) => {
      assignSplitsChangedRef.current = true;
      if (!val) {
        const nextSplit = { items: null } satisfies PlainSplitProps;
        pendingAssignSplitsSplitRef.current = nextSplit;
        void form.setters.setFieldValue('transaction.split', nextSplit);
        return;
      }
      const rawSplit = splitToPlain(val);
      const nextSplit = rawSplit ? markOverflowItemsInSplit(rawSplit) : ({ items: null } satisfies PlainSplitProps);
      pendingAssignSplitsSplitRef.current = nextSplit;
      void form.setters.setFieldValue('transaction.split', nextSplit);
    },
    [form.setters],
  );

  const assignSplitsDialogId = `SPLIT_DIALOG_SERIES_${
    scheduledTransactionPlain?.id || scheduledTransactionPlain?.clientId || 'new'
  }`;
  const closeSplitDialog = useCallback(
    () => dispatch(rootUiActions.removeDialog(assignSplitsDialogId, { animate: true })),
    [assignSplitsDialogId, dispatch],
  );

  const onBackToScheduledTxn = useCallback(() => {
    if (assignSplitsChangedRef.current) {
      const currentTransaction = form.values.transaction;
      void form.setters.setFieldValue(
        'transaction.split',
        pendingAssignSplitsSplitRef.current ?? currentTransaction?.split,
      );
      void form.setters.setFieldValue('transaction.coa', pendingAssignSplitsCoaRef.current ?? currentTransaction?.coa);
      void form.setters.setFieldValue(
        'transaction.tags',
        pendingAssignSplitsTagsRef.current ?? currentTransaction?.tags,
      );
      if (initialShowAssignSplits) {
        void formSubmit.submit(true);
        return;
      }
      // Callers without their own explicit save step (e.g. BillScoutSetup's
      // per-field autosave) need to be told the split changed here — every
      // other field commit already goes through this same "notify on edit"
      // path, but split/coa/tags are set via refs during the nested split
      // dialog rather than through a field setter callers can hook.
      onSplitsChanged?.();
      setShowAssignSplits(false);
      return;
    }
    if (initialShowAssignSplits) {
      onClose(false);
      return;
    }
    setShowAssignSplits(false);
  }, [form, formSubmit, initialShowAssignSplits, onClose, onSplitsChanged]);

  const openAssignSplitsDialog = useCallback(() => {
    if (!splitSeriesFf) {
      return;
    }

    assignSplitsChangedRef.current = false;
    pendingAssignSplitsSplitRef.current = undefined;
    pendingAssignSplitsCoaRef.current = undefined;
    pendingAssignSplitsTagsRef.current = undefined;
    openedAssignSplitsDialogIdRef.current = assignSplitsDialogId;
    setShowAssignSplits(true);
    const currentTransaction = form.values.transaction as TransactionProps;
    const currentSplitRecord = currentTransaction.split
      ? (transactionsTypes.mkSplit(currentTransaction.split as SplitProps) as RecordOf<SplitProps>)
      : null;
    const txnForSplitDialog: TransactionProps = {
      ...currentTransaction,
      split: currentSplitRecord ?? undefined,
    };

    dispatch(
      showSplitDialog({
        id: assignSplitsDialogId,
        mode: 'series',
        txn: txnForSplitDialog,
        setSplit: setSeriesSplit,
        setAmount: (val: number) => form.setters.setFieldValue('transaction.amount', val),
        setCoa: (newCOA) => {
          pendingAssignSplitsCoaRef.current = newCOA ?? null;
          void form.setters.setFieldValue('transaction.coa', newCOA ?? null);
        },
        setTags: (val) => {
          pendingAssignSplitsTagsRef.current = val;
          void form.setters.setFieldValue('transaction.tags', val);
        },
        accountId: currentTransaction.accountId ?? '',
        disabledPaymentFn: () => !doesSplitModeAllowPaymentOrnaments('series'),
        setCustomersAndProjects: noopSetCustomersAndProjects,
        rootModalDrawer,
        onBack: () => {
          closeSplitDialog();
          onBackToScheduledTxn();
        },
        onClose: () => {
          closeSplitDialog();
          onClose(form.derived.dirty);
        },
      }),
    );
  }, [
    assignSplitsDialogId,
    closeSplitDialog,
    dispatch,
    form,
    noopSetCustomersAndProjects,
    onBackToScheduledTxn,
    onClose,
    rootModalDrawer,
    setSeriesSplit,
    splitSeriesFf,
  ]);
  openAssignSplitsDialogRef.current = openAssignSplitsDialog;

  const forceSubmit = useCallback(() => {
    void formSubmit.submit(true);
  }, [formSubmit]);

  const submit = useCallback(() => {
    void formSubmit.submit();
  }, [formSubmit]);

  const manualSeriesApiCall = useCallback(() => {
    const { values } = form;
    dispatchRequestDialog({
      method: 'PUT',
      path: `/scheduled-transactions/${values?.id}`,
      body: JSON.stringify(values, null, 2),
    });
  }, [form]);

  const generateSeriesInstances = useCallback(() => {
    const { values } = form;
    const txns = [
      {
        ...values.transaction,
        state: 'CLEARED',
      },
    ];
    dispatchGeneratorDialog({ txns, start: values.dueOn?.toISODate() });
  }, [form]);

  const generateSeriesTransferInstances = useCallback(() => {
    const { values } = form;
    const currentTxn = values.transaction;
    const txns = [
      {
        ...currentTxn,
        state: 'CLEARED',
      },
      {
        ...currentTxn,
        accountId: currentTxn.coa?.id,
        coa: { type: 'ACCOUNT', id: currentTxn.accountId },
        amount: -currentTxn.amount,
        state: 'CLEARED',
      },
    ];
    dispatchGeneratorDialog({ txns, start: values.dueOn?.toISODate() });
  }, [form]);

  const openAssignSplits = useCallback(() => {
    if (splitSeriesFf) {
      openAssignSplitsDialog();
    }
  }, [openAssignSplitsDialog, splitSeriesFf]);

  return useMemo<ScheduledTransactionEditViewModel>(
    () => ({
      actions: {
        confirmDelete,
        confirmEndSeries,
        forceSubmit,
        generateSeriesInstances,
        generateSeriesTransferInstances,
        handleSubmit: formSubmit.handleSubmit,
        manualSeriesApiCall,
        onClose: close,
        onCloseClean: closeClean,
        openAssignSplits,
        setTabValue,
        submit,
      },
      derived: {
        create,
        surfaceMode,
        minimumDate,
        nestedRootModalDrawer,
        open,
        rootModalDrawer,
        scheduledTransaction: scheduledTransactionPlain,
        showAssignSplits,
        showBackButton: Boolean(onBack),
        showBillTab,
        subscriptionInitial,
        tabValue,
        title,
      },
      form,
      navigation: {
        onBack,
      },
    }),
    [
      close,
      closeClean,
      confirmDelete,
      confirmEndSeries,
      create,
      forceSubmit,
      form,
      formSubmit.handleSubmit,
      generateSeriesInstances,
      generateSeriesTransferInstances,
      surfaceMode,
      manualSeriesApiCall,
      minimumDate,
      nestedRootModalDrawer,
      onBack,
      open,
      openAssignSplits,
      rootModalDrawer,
      scheduledTransactionPlain,
      showAssignSplits,
      showBillTab,
      subscriptionInitial,
      submit,
      tabValue,
      title,
    ],
  );
}
